import { useCallback, useRef } from "react"
import {
	Stack,
	Button, IconButton, Checkbox, Typography,
	ListItemButton, ListItemText, Divider,
	Tooltip
} from "@mui/material"
import { CloseIcon } from "shared/icons"
import {
	ViewColumn as ViewColumnIcon,
	Replay as ReplayIcon,
	PushPin as StickyIcon,
	PushPinOutlined as NoStickyIcon 
} from '@mui/icons-material'

import { SortableList, useSortableList } from "components/controls/sortable/list"
import { GridColumnDefMap, GridColumnState } from "../../types"

type GridColumnManageInspectorProps<T> = {
	columnsDef: GridColumnDefMap<T>
	columnsState: GridColumnState[]
	setColumnsState: (columnsState: GridColumnState[]) => void
	onClose: () => void
}
export const GridColumnManageInspector = <T,>({
	columnsDef,
	columnsState,
	setColumnsState,
	onClose
}: GridColumnManageInspectorProps<T>) => {
  const sortableId = useRef<number>(0)
	const toSortable = useCallback(
		(state: GridColumnState) => {
			sortableId.current = sortableId.current + 1
			return {
				...state,
				id: sortableId.current,
				draggable: undefined
			}
		},
		[sortableId]
	)

	const sortable = useSortableList({
		list: columnsState,
		toSortable,
		onChangeList: list => setColumnsState(list)
	})

	const onClickSticky = useCallback(
		(field: string, sticky: boolean) => {
			const newColumnsState = columnsState.map(
				state => state.field !== field ? state : ({ ...state, sticky })
			).slice()
			setColumnsState(newColumnsState)
			sortableId.current = 0
			sortable.setSortableList(newColumnsState.map(toSortable))
		},
		[columnsState, setColumnsState, toSortable, sortable]
	)

	const onClickColumnShow = useCallback(
		(field: string, show: boolean) => {
			const newColumnsState = columnsState.map(
				state => state.field !== field ? state : ({ ...state, show })
			).slice()
			setColumnsState(newColumnsState)
			sortableId.current = 0
			sortable.setSortableList(newColumnsState.map(toSortable))
		},
		[columnsState, setColumnsState, toSortable, sortable]
	)

	const onReset = useCallback(
		() => {
			const newColumnsState = Object.entries(columnsDef).flatMap(
				([field, column]) => {
					if (column.hidden === true) return []
					return {
						field,
						show: column.defaultShow ?? true,
						hideable: column.hideable === false ? false : true,
						sticky: column.sticky,
						width: column.width
					}
				}
			)
			setColumnsState(newColumnsState)
			sortableId.current = 0
			sortable.setSortableList(newColumnsState.map(toSortable))
		},
		[columnsDef, setColumnsState, toSortable, sortable]
	)

	return (
		<Stack width='100%' height='100%' boxSizing="border-box">
			<Stack direction="row" alignItems="center" paddingY={0.5} paddingX={1}>
				<ViewColumnIcon fontSize="small" sx={{ marginRight: 0.5 }}/>
				<Typography component="div" flexGrow={1} variant="body2">列管理</Typography>
				<IconButton size="small" onClick={onClose}>
					<CloseIcon fontSize="small"/>
				</IconButton>
			</Stack>
			<Divider/>
			<SortableList
				{...sortable}
				listProps={{
					disablePadding: true,
					sx: { flexGrow: 1, overflowY: 'auto', width: '100%' }
				}}
				hasGrab
				renderItem={state => (
					<Stack direction="row" width="100%">
						<ListItemButton
							key={`field-${state.field}`} 
							dense disableGutters sx={{ padding: 0, flexGrow: 1 }}
							onClick={() => {
								if (!state.hideable) return
								onClickColumnShow(state.field, state.show ? false : true)
							}}
						>
							<Checkbox
								size="small"
								checked={state.show}
								disabled={state.hideable === false}
								tabIndex={-1}
								disableRipple
								inputProps={{ 'aria-labelledby': `check-${state.field}` }}
							/>
							<ListItemText id={`check-${state.field}`} primary={columnsDef[state.field]?.headerName ?? ''} />
						</ListItemButton>

						<Tooltip title="固定" placement="top">
							<Checkbox
								size="small"
								checked={state.sticky === true}
								tabIndex={-1}
								disableRipple
								onClick={() => {
									onClickSticky(state.field, state.sticky ? false : true)
								}}
								icon={<NoStickyIcon/>}
								checkedIcon={<StickyIcon/>}
							/>
						</Tooltip>
					</Stack>
				)}
			/>
			<Divider/>
			<Stack direction="row" spacing={1} padding={1}>
				<Button
					fullWidth
					variant="outlined"
					startIcon={<ReplayIcon />}
					sx={{ whiteSpace: 'nowrap' }}
					onClick={onReset}
				>
					初期状態に戻す
				</Button>
			</Stack>
		</Stack>
	)
}
